import request from '@/utils/api/request';
import AddContractorsModal from '@/containers/contractors/add-contractors-modal/AddContractorsModal';
import { CONTRACTORS_MODAL_STEPS } from '@/utils/constants/contractors-steps';

const CONTACT_INITIAL = {
  name: '',
  position: '',
  phone_number: '',
  email: '',
};

const INITIAL_FORM = {
  name: '',
  address: '',
  registration_number: '',
  company_domain: '',
  language: 'en',
  is_supplies_materials_only: false,
  contacts: [],
  works: [],
};

export const state = () => ({
  contractors: [],
  nextUrl: null,
  workGroups: [],
  form: { ...INITIAL_FORM },
  contractorTenders: [],
  errors: {},
  modalLoading: false,
  tenderLoading: true,
});

export const mutations = {
  updateFormValue(state, { key, value }) {
    state.form[key] = value;
  },
  setNextUrl(state, url) {
    state.nextUrl = url;
  },
  setContractors(state, contractors) {
    state.contractors = contractors;
  },
  setContractorInfo(state, contractorInfo) {
    state.form = contractorInfo;
  },
  setContractorWorks(state, workGroups) {
    state.workGroups = workGroups;
  },
  setContractorTenders(state, tenders) {
    state.contractorTenders = tenders;
  },
  insertUpdatedContractor(state, updatedContractor) {
    const updatedIndex = state.contractors.findIndex((elem) => elem.id === updatedContractor.id);
    if (updatedIndex > -1) {
      state.contractors.splice(updatedIndex, 1, updatedContractor);
    }
  },
  clearContractorInfo(state) {
    state.form = { ...INITIAL_FORM };
    state.contractorTenders = [];
    state.errors = {};
    state.tenderLoading = true;
  },
  addContactRow(state) {
    state.form.contacts.push({ ...CONTACT_INITIAL });
  },
  updateContactInfo(state, params) {
    state.form.contacts[params.index][params.key] = params.value;
  },
  removeContactRow(state, index) {
    state.form.contacts.splice(index, 1);
    if (state.errors.contacts) {
      delete state.errors.contacts;
    }
  },
  setErrors(state, errors) {
    state.errors = errors;
  },
  insertContractor(state, newContractor) {
    state.contractors = [newContractor, ...state.contractors];
  },
  setModalLoading(state, status) {
    state.modalLoading = status;
  },
  setTenderLoading(state, status) {
    state.tenderLoading = status;
  },
};

export const actions = {
  clearContractorInfo({ commit }) {
    commit('clearContractorInfo');
  },
  updateFormValue({ commit }, params) {
    commit('updateFormValue', params);
  },
  async loadContractors({ commit }, { limit }) {
    try {
      const { data } = await request.get('/contractors/', {
        params: { limit },
      });
      commit('setContractors', data.results);
      commit('setNextUrl', data.next);
    } catch (e) {
      console.error(e);
    }
  },
  async loadContractorWorks({ commit }) {
    try {
      const { data } = await request.get('/contractors/enums/');
      commit('setContractorWorks', data.work_groups);
    } catch (e) {
      console.error(e);
    }
  },
  async createContractor({ dispatch, state, commit }) {
    try {
      const { data } = await request.post('/contractors/', state.form);
      commit('insertContractor', data);
      dispatch(
        'modal/openModal',
        {
          component: AddContractorsModal,
          context: {
            isEdit: true,
            id: data.id,
            step: CONTRACTORS_MODAL_STEPS.GENERAL,
          },
        },
        { root: true }
      );
      dispatch('loadContractorInfo', data.id);
    } catch (e) {
      commit('setErrors', e.response.data);
      console.error(e);
    }
  },
  async createTenderContractor({ dispatch, state, commit }) {
    try {
      const { data } = await request.post('/contractors/', state.form);
      dispatch(
        'tenderEdit/addContractor',
        { contractor_responsible_users: data.contacts, contractor_team: data },
        { root: true }
      );
      dispatch('modal/closeModal', {}, { root: true });
      commit('clearContractorInfo');
    } catch (e) {
      commit('setErrors', e.response.data);
      console.error(e);
    }
  },
  async loadContractorInfo({ commit, dispatch }, id) {
    commit('setModalLoading', true);
    try {
      const { data } = await request.get(`/contractors/${id}/`);
      commit('setContractorInfo', data);
    } catch (e) {
      dispatch('alert/showAlert', { type: 'error' }, { root: true });
    }
    commit('setModalLoading', false);
  },
  async loadContractorTenders({ commit, dispatch }, id) {
    commit('setTenderLoading', true);
    try {
      const { data } = await request.get(`/contractors/${id}/tenders/`);
      commit('setContractorTenders', data);
    } catch (e) {
      dispatch('alert/showAlert', { type: 'error' }, { root: true });
    }
    commit('setTenderLoading', false);
  },
  async saveContractorChanges({ dispatch, state, commit }) {
    try {
      const { data } = await request.put(`/contractors/${state.form.id}/`, state.form);
      commit('insertUpdatedContractor', data);
      dispatch('modal/closeModal', {}, { root: true });
    } catch (e) {
      commit('setErrors', e.response.data);
      console.error(e);
    }
  },
  closeAddContractorModal({ commit, dispatch }) {
    commit('clearContractorInfo');
    dispatch('modal/closeModal', {}, { root: true });
  },
  addContactRow({ commit }) {
    commit('addContactRow');
  },
  updateContactInfo({ commit }, params) {
    commit('updateContactInfo', params);
  },
  removeContactRow({ commit }, index) {
    commit('removeContactRow', index);
  },
};

export default { namespaced: true, state, mutations, actions };
